import moment from "moment";
import { isPresent } from 'utils/isPresent';
import { createSelector } from 'reselect'
import Api from './Api.js'
import Utils from '../Common/Utils.js'
import { EmptyObject, EmptyArray } from '../../Resources/EmptyDefaults'
import { LocalStorage } from '../Common/Utils/local_storage'
import { parseQueryString } from 'components/Common/ParameterUtilities';
import { formatCurrency } from 'utils/currency'
import { isBlank } from 'utils/isBlank'

export const JOBS_LIST = 'jobsList'
export const DEFAULT_PAGE = 1
export const UNSENT = 'Unsent'
export const DEFAULT_PAGE_SIZE = 50
const BASE_TEN = 10

export const getJobs = (state) => state[JOBS_LIST] || {}

export const getJobsData = createSelector(
  getJobs,
  (jobs) => jobs.data || {}
)

export const getJobsList = createSelector(
  getJobsData,
  jobsData => jobsData.data || []
)

export const getCurrentPage = createSelector(
  getJobs,
  (jobs) => parseInt(jobs.page, BASE_TEN) || 1
)

export const getPageSize = createSelector(
  getJobs,
  (jobs) => parseInt(jobs.page_size, BASE_TEN) || parseInt(LocalStorage.getItem('invoices_table_page_size'), BASE_TEN) || DEFAULT_PAGE_SIZE
)

export const getTotalCount = createSelector(
  getJobs,
  (jobs) => jobs.total_count || 0
)

export const getExpandedRows = createSelector(
  getJobs,
  (jobs) => jobs.expandedRows || {}
)

export const getRowExpanded = jobUuid => createSelector(
  getExpandedRows,
  (expandedRows) => {
    return expandedRows[jobUuid] || false
  }
)

export const getDeletedRows = createSelector(
  getJobs,
  (jobs) => jobs.deletedRows || {}
)

export const getRowDeleted = jobUuid => createSelector(
  getDeletedRows,
  (deletedRows) => {
    return deletedRows[jobUuid] || false
  }
)

export const getUuidToRestore = createSelector(
  getJobs,
  (jobs) => jobs.uuidToRestore || ''
)

export const getShowInvoicesTableSnackbar = createSelector(
  getJobs,
  (jobs) => jobs.showInvoicesTableSnackbar || false
)

export const getShowSnackBarSpinner = createSelector(
  getJobs,
  (jobs) => jobs.showSnackBarSpinner || false
)

export const getShowActionBar = createSelector(
  getJobs,
  (jobs) => jobs.showActionBar || false
)

export const getShowSpinner = createSelector(
  getJobs,
  (jobs) => jobs.showInvoicesTableSpinner|| false
)

export const getSubmittingRows = createSelector(
  getJobs,
  (jobs) => jobs.submittingRows || {}
)

export const getRowSubmitting = jobUuid => createSelector(
  getSubmittingRows,
  (submittingRows) => {
    return submittingRows[jobUuid] || false
  }
)

const formatDate = (date) => {
  const dateWithoutTime = date.includes('T') ? date.split('T')[0] : date
  return moment(dateWithoutTime).format('MM/DD/YYYY')
}

const isADate = (date) => {
  const dateRegex =  /^\d{4}-\d{2}-\d{2}$/
  return dateRegex.test(date)
}

export const getInvoiceId = (job) => job.invoice_number || ''
export const getJobUuid = (job) => job.id || ''
export const getJobTech = (job) => job.pro_name || ''
export const getOutstandingBalance = (job) => formatCurrency(job.outstanding_balance / 100)
export const hasBalance = (job) => job.outstanding_balance > 0
export const getJobFinished = (job) => isPresent(job.job_finished_at) ? formatDate(job.job_finished_at) : '--'
export const getLastSent = (job) => isPresent(job.invoice_last_sent_at) ? formatDate(job.invoice_last_sent_at) : '--'
export const getNextReminderDate = (job) => isPresent(job.next_invoice_reminder_date) ? formatDate(job.next_invoice_reminder_date) : '--'
export const getDueDate = (job) => isPresent(job.due_date) ? formatDate(job.due_date) : '--'
export const getTerms = (job) =>  {
  if(isADate(job.terms)) {
    return formatDate(job.terms)
  }
  return job.terms || ''
}
export const getNotes = (job) => job.notes || '--'
export const getOutstandingBalanceNonformatted = (job) => job.outstanding_balance || 0

const getCustomerInfo = (customer) => isPresent(customer) ? customer : {}
export const getCustomerName = (job) => getCustomerInfo(job.customer).display_name || ''
export const getCustomerUuid = (job) => getCustomerInfo(job.customer).id || ''
export const getCustomerEmail = (job) => getCustomerInfo(job.customer).email || ''
export const getCustomerMobileNumber = (job) => getCustomerInfo(job.customer).mobile_number || ''

export const getAddress = (job) => isPresent(job.address) ? job.address.data : {}
export const getStreetAddress = (job) => getAddress(job).street || '--'
export const getCityStateZip = (job) => {
  const address = getAddress(job)
  if (isBlank(address)) {
    return ''
  }
  return `${address.city}, ${address.state} ${address.zip}`
}
export const getMobileDate = (job) => {
  if (isPresent(job.invoice_last_sent_at)) {
    return `last sent ${moment(job.invoice_last_sent_at).format('MM/DD/YYYY')}`
  } else if (isPresent(job.job_finished_at)) {
    return `finished ${moment(job.job_finished_at).format('MM/DD/YYYY')}`
  } else {
    return ''
  }
}
export const getStatus = (job) => job.status || ''
export const getStatusColor = job => {
  const status = getStatus(job);
  switch(status) {
    case 'Due':
      return 'alert'
    case 'Past due':
      return 'error'
    case UNSENT:
      return 'primary'
    default:
      return 'primary'
  }
}

export const openInvoiceFilters = () => {
  const urlParams = parseQueryString(window.location.search)
  const openInvoiceFilter = { invoice_status: 'due,past_due,unsent,due_upon_completion_of_work' }
  return { ...openInvoiceFilter,
          ...urlParams }
}

export const fetchOpenInvoices = (page, pageSize) => ({
  fetchingApiFunc: Api.fetchJobsList,
  selector: getJobs,
  paramsFromProps: () => {
    const filters = openInvoiceFilters()
    return [page || DEFAULT_PAGE, pageSize, filters]
  },
  dataNamespace: JOBS_LIST,
})

export const getSelectedRows = createSelector(
  getJobs,
  (jobs) => jobs.selectedRows || EmptyObject
)

export const getSelectedRowsIds = createSelector(
  getSelectedRows,
  selectedRows => {
    return isPresent(selectedRows) ? Object.keys(selectedRows) : EmptyArray
  }
)

export const getNumberOfSelectedRows = createSelector(
  getSelectedRowsIds,
  selectedRowIds => (selectedRowIds.length || 0)
)

export const getIsSelected = createSelector(
  getNumberOfSelectedRows,
  numberOfRows => numberOfRows > 0 || false
)

export const getCheckedState = createSelector(
  [getJobsList, getSelectedRows],
  (jobs, selectedRows) => {
    return isPresent(jobs) && jobs.every((job) => selectedRows[job.id])
  }
)
